import logoSrc from '/logo.png';
import { IconButton, InputAdornment } from '@mui/material';
import AccountMenu from './account-menu';
import { Link } from 'react-router-dom';
import customTwMerge from 'utils/twMerge';
import { GridMenuIcon } from '@mui/x-data-grid';
import { useAppDispatch } from 'hooks/redux';
import { setSidebarOpen } from 'store/app/app-slice';
import LanguageSelect from '../select/language-select';
import COutlinedInput from '../input/outlined-input';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MagnifierSvg } from 'assets/svg/magnifier.svg';
import { useSearchQuery } from 'services/dropbox';
import RightDrawer from '../drawer/right-drawer';
import SearchContent from 'components/pages/dashboard/search-content';

type Props = { isAuthPage?: boolean; className?: string };

export default function Header({ isAuthPage, className }: Props) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('common');

  const initialSearchState: {
    key: string;
    state: boolean;
    result: null | SearchResponse[];
  } = {
    key: '',
    state: false,
    result: null,
  };
  const [search, setSearch] = useState(initialSearchState);

  const handleClick = () => {
    if (search.key.trim()) {
      setSearch((prev) => ({ ...prev, state: true }));
    }
  };

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setSearch((prev) => ({ ...prev, key: event.target.value, state: false }));
  };

  const { data, isFetching } = useSearchQuery(
    {
      search: search.key.trim(),
      keys: ['DoctorKeys', 'DepartmentKeys'],
    },
    { skip: !search.state }
  );

  useEffect(() => {
    setSearch((prev) => ({ ...prev, result: data?.data || null }));
  }, [data, isFetching]);

  return (
    <header
      className={customTwMerge(
        'flex h-[80px] w-full justify-between rounded-lg bg-neutral-0 px-4 py-4 shadow-sm md:px-6 lg:px-9',
        className
      )}
    >
      {isAuthPage ? (
        <>
          <Link to="/" className="self-center">
            <img src={logoSrc} alt="VetAmerikan Logo" className="w-[200px] object-contain" />
          </Link>

          <LanguageSelect />
        </>
      ) : (
        <div className="flex w-full justify-between gap-2">
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => dispatch(setSidebarOpen('toggle'))}
            className="lg:hidden"
          >
            <GridMenuIcon />
          </IconButton>

          <COutlinedInput
            id="search"
            label={t('search')}
            type="search"
            name="search"
            placeholder={t('search-placeholder')}
            value={search.key}
            onChange={handleChange}
            onKeyDown={(event) => (event.key === 'Enter' ? handleClick() : null)}
            className="w-full max-w-[324px]"
            InputProps={{
              startAdornment: (
                <InputAdornment
                  position="start"
                  className="hover-transition w-5 shrink-0"
                  onClick={() => handleClick()}
                >
                  <MagnifierSvg />
                </InputAdornment>
              ),
            }}
          />

          <AccountMenu />
        </div>
      )}

      <RightDrawer
        isOpen={search.state}
        onClose={() => setSearch((prev) => ({ ...prev, state: false }))}
        title={t('search-results')}
        content={
          <SearchContent
            isLoading={isFetching}
            content={search.result}
            closeDrawer={() => setSearch((prev) => ({ ...prev, key: '', state: false }))}
          />
        }
      />
    </header>
  );
}

